import { AppRouteMeta } from '@sennder/shell-utilities'
import {
  checkMarketplaceAccess,
  checkOnboardingAccess,
  redirectOrderWizardLinks,
  restrictForNonCompanyCarrier,
} from './route-guards'
import {
  MFRepositories,
  ApplicationTypes,
  getRenderer as getRegistryRenderer,
} from '@sennder/microfrontend-registry'

/**
 * Unique application route
 */
export type AppRoute = keyof typeof routes

export const getRenderer = <
  TRepo extends MFRepositories,
  TApp extends keyof ApplicationTypes[TRepo]
>(
  Repo: TRepo,
  App: TApp
) =>
  getRegistryRenderer<TRepo, TApp, unknown>(Repo, App, {
    integrator: 'orcas',
  })

export const TERMS_ROUTE_NAME = 'TermsAndConditions'
export const ONBOARDING_ROUTE_NAME = 'Onboarding'

/**
 * All routes of the application, ordered alphabetically by path.
 * Each route has navigation guards, analytical and logging contexts.
 */
export const routes = {
  '/assignments': {
    name: 'Assignments',
    renderer: getRenderer(MFRepositories.OrcasAssignments, 'page'),
    context: {
      analytics: {
        module: 'assignments',
        submodule: '',
      },
      logger: {
        codeOwners: 'matching',
        module: 'assignments-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/fleet': {
    name: 'Fleet',
    renderer: getRenderer(MFRepositories.Fleet, 'page'),
    context: {
      analytics: {
        module: 'fleet',
        submodule: '',
      },
      logger: {
        codeOwners: 'transport-planning',
        module: 'fleet-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/marketplace': {
    name: 'Marketplace',
    renderer: getRenderer(MFRepositories.Marketplace, 'page'),
    context: {
      analytics: {
        module: 'marketplace',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-discovery',
        module: 'marketplace-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier, checkMarketplaceAccess],
  },
  '/marketplace/tenders': {
    name: 'Tenders',
    renderer: getRenderer(MFRepositories.Marketplace, 'page'),
    context: {
      analytics: {
        module: 'tenders',
        submodule: '',
      },
      logger: {
        codeOwners: 'matching',
        module: 'assignments-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier, checkMarketplaceAccess],
  },
  '/onboarding': {
    name: ONBOARDING_ROUTE_NAME,
    renderer: getRenderer(MFRepositories.Onboarding, 'page'),
    context: {
      analytics: {
        module: 'onboarding',
        submodule: '',
      },
      logger: {
        codeOwners: 'onboarding',
        module: 'onboarding-component',
      },
    },
    guards: [restrictForNonCompanyCarrier, checkOnboardingAccess],
  },
  '/order-details/:idForStaff': {
    name: 'OrderDetails',
    renderer: getRenderer(MFRepositories.Marketplace, 'page'),
    context: {
      analytics: {
        module: 'order-details',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-discovery',
        module: 'marketplace-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/orders': {
    name: 'Orders',
    renderer: getRenderer(MFRepositories.Transport, 'page'),
    context: {
      analytics: {
        module: 'orders',
        submodule: '',
      },
      logger: {
        codeOwners: 'transport-planning',
        module: 'transport-mf-component',
      },
    },
    guards: [redirectOrderWizardLinks, restrictForNonCompanyCarrier],
  },
  '/payments': {
    name: 'Payments',
    renderer: getRenderer(MFRepositories.PaymentOverview, 'page'),
    context: {
      analytics: {
        module: 'payments',
        submodule: '',
      },
      logger: {
        codeOwners: 'order-fulfillment',
        module: 'payments-overview-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/profile': {
    name: 'Profile',
    renderer: getRenderer(MFRepositories.Profile, 'page'),
    context: {
      analytics: {
        module: 'account',
        submodule: '',
      },
      logger: {
        codeOwners: 'onboarding',
        module: 'profile-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/sennfuel': {
    name: 'SennFUEL',
    renderer: getRenderer(MFRepositories.SennFuel, 'page'),
    context: {
      analytics: {
        module: 'sennfuel',
        submodule: '',
      },
      logger: {
        codeOwners: 'sennfuel',
        module: 'sennfuel-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/sennfuel-lp': {
    name: 'SennFUELLP',
    renderer: getRenderer(MFRepositories.SennFuel, 'page'),
    public: true,
    fullscreenLayout: true,
    context: {
      analytics: {
        module: 'sennfuel-lp',
        submodule: '',
      },
      logger: {
        codeOwners: 'sennfuel',
        module: 'sennfuel-mf-component',
      },
    },
  },
  '/tender-details/:id': {
    name: 'TenderDetails',
    renderer: getRenderer(MFRepositories.Marketplace, 'page'),
    context: {
      analytics: {
        module: 'tender-details',
        submodule: '',
      },
      logger: {
        codeOwners: 'matching',
        module: 'assignments-mf-component',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
  '/terms': {
    name: TERMS_ROUTE_NAME,
    renderer: getRenderer(MFRepositories.Terms, 'page'),
    context: {
      analytics: {
        module: 'terms',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'terms',
      },
    },
    guards: [restrictForNonCompanyCarrier],
  },
} as const satisfies {
  [TRoute: string]: AppRouteMeta
}
